import classNames from 'classnames/bind';
import * as dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React from 'react';

import LoadingScreen from 'components/Shared/LoadingScreen';
import Button from 'uikit/Button';

import FutureCourse from './FutureCourse';
import PastCourse from './PastCourse';
import styles from './Agenda.module.scss';
import CourseItem from './CourseItem';

const cx = classNames.bind(styles);

const Agenda = ({
  agenda,
  handleDaySelect,
  handleWeekCurrent,
  handleWeekNext,
  handleWeekPrevious,
  isLookingCurrentWeek,
  isSingle,
  selectedDay,
  t,
}) => (
  <>
    {!agenda ? (
      <LoadingScreen />
    ) : (
      <div className={cx('container')}>
        <div className={cx('header')}>
          <p className={cx('period')}>{`${dayjs(
            agenda.statistics[0].date,
          ).format('MMMM')} ${dayjs(agenda.statistics[0].date).format(
            'DD',
          )}-${dayjs(agenda.statistics[6].date).format('DD')}, ${dayjs(
            agenda.statistics[0].date,
          ).format('YYYY')}`}</p>
          <div className={cx('actions')}>
            {!isLookingCurrentWeek && (
              <Button
                className={cx('action', 'action-today')}
                onClick={handleWeekCurrent}
              >
                {t('Agenda/current-week')}
              </Button>
            )}
            <Button
              aria-label={t('Agenda/aria-previous-week')}
              className={cx('action', 'before')}
              icon="chevron-left"
              onClick={handleWeekPrevious}
            />
            <Button
              aria-label={t('Agenda/aria-next-week')}
              className={cx('next')}
              icon="chevron-right"
              onClick={handleWeekNext}
            />
          </div>
        </div>
        <div className={cx('week-large')}>
          {agenda.statistics.map((day, dayIndex) => (
            <div
              className={cx('day', {
                first: dayIndex === 0,
                last: dayIndex === 6,
              })}
              key={day.date}
            >
              <div
                className={cx('day-header', {
                  'future-day': !day.isDayPast && !day.isToday,
                  first: dayIndex === 0,
                  last: dayIndex === 6,
                })}
              >
                <div
                  className={cx('date-number', {
                    today: day.isToday,
                  })}
                >
                  {dayjs(day.date).format('DD')}
                </div>
                <div
                  className={cx('date-day', {
                    today: day.isToday,
                  })}
                >
                  {dayjs(day.date).format('ddd')}
                </div>
              </div>
              <div
                className={cx('day-courses', {
                  first: dayIndex === 0,
                  last: dayIndex === 6,
                })}
              >
                {day.coursesOfTheDay.map((course) => (
                  <div key={`${course.id}_${day.date}`}>
                    {isSingle ? (
                      !course.isPast ? (
                        <FutureCourse course={course} isSingle={isSingle} />
                      ) : (
                        <PastCourse course={course} isSingle={isSingle} />
                      )
                    ) : (
                      <CourseItem
                        course={course}
                        day={day}
                        isSingle={isSingle}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className={cx('week-small')}>
          <div className={cx('week-small-header')}>
            {agenda.statistics.map((day) => (
              <button
                className={cx('day-header')}
                key={day.date}
                onClick={() => handleDaySelect(day)}
                type="button"
              >
                <div
                  className={cx('date-number', {
                    today:
                      dayjs(selectedDay?.date).format('DD') ===
                      dayjs(day.date).format('DD'),
                  })}
                >
                  {dayjs(day.date).format('DD')}
                </div>
                <div
                  className={cx('date-day', {
                    today:
                      dayjs(selectedDay?.date).format('DD') ===
                      dayjs(day.date).format('DD'),
                  })}
                >
                  {dayjs(day.date).format('ddd')}
                </div>
              </button>
            ))}
          </div>
          <div className={cx('day-courses')}>
            {selectedDay && (
              <>
                {selectedDay.coursesOfTheDay.map((course) => (
                  <div key={`${course.id}_${selectedDay.date}`}>
                    {isSingle ? (
                      !course.isPast ? (
                        <FutureCourse course={course} isSingle={isSingle} />
                      ) : (
                        <PastCourse course={course} isSingle={isSingle} />
                      )
                    ) : (
                      <CourseItem
                        course={course}
                        day={selectedDay}
                        isSingle={isSingle}
                      />
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    )}
  </>
);

Agenda.propTypes = {
  agenda: PropTypes.object,
  handleDaySelect: PropTypes.func.isRequired,
  handleWeekCurrent: PropTypes.func.isRequired,
  handleWeekNext: PropTypes.func.isRequired,
  handleWeekPrevious: PropTypes.func.isRequired,
  isLookingCurrentWeek: PropTypes.bool.isRequired,
  isSingle: PropTypes.bool,
  selectedDay: PropTypes.object,
  t: PropTypes.func.isRequired,
};

Agenda.defaultProps = {
  agenda: null,
  selectedDay: undefined,
  isSingle: undefined,
};

export default Agenda;
